import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: 'Facebook', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  { id: 'instagram', label: 'Instagram', icon: 'Instagram', color: 'text-pink-600', bg: 'bg-pink-50 border-pink-200' },
  { id: 'google', label: 'Google Ads', icon: 'Search', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  { id: 'all', label: 'All Platforms', icon: 'Globe', color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200' },
];

const CreateCampaignTab = ({ orgId, organization, onCampaignCreated }) => {
  const [platform, setPlatform] = useState('facebook');
  const [prompt, setPrompt] = useState('');
  const [budget, setBudget] = useState('');
  const [location, setLocation] = useState('');
  const [generating, setGenerating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [adCopies, setAdCopies] = useState(null);
  const [audienceSuggestions, setAudienceSuggestions] = useState(null);
  const [budgetRecommendation, setBudgetRecommendation] = useState(null);
  const [campaignName, setCampaignName] = useState('');

  const handleGenerate = async () => {
    if (!prompt?.trim()) { toast?.error('Please describe your campaign goal'); return; }
    setGenerating(true);
    setAdCopies(null);
    setAudienceSuggestions(null);
    setBudgetRecommendation(null);

    try {
      const systemPrompt = `You are an expert digital advertising copywriter and media buyer. Generate ad content in valid JSON format only. No markdown, no explanation, just JSON.`;
      const userPrompt = `Create a ${platform === 'all' ? 'multi-platform' : platform} ad campaign for:
Goal: ${prompt}
Budget: ${budget ? `$${budget}/day` : 'not specified'}
Location: ${location || 'not specified'}

Return ONLY this JSON structure:
{
  "adCopies": [
    {"headline": "...", "description": "...", "callToAction": "..."},
    {"headline": "...", "description": "...", "callToAction": "..."},
    {"headline": "...", "description": "...", "callToAction": "..."}
  ],
  "audienceSuggestions": {
    "ageRange": "...",
    "interests": ["...", "...", "..."],
    "behaviors": ["...", "..."],
    "demographics": "..."
  },
  "budgetRecommendation": {
    "daily": "...",
    "monthly": "...",
    "reasoning": "..."
  },
  "campaignName": "..."
}`;

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        { max_tokens: 1500, temperature: 0.7 }
      );

      const content = response?.choices?.[0]?.message?.content || '';
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid response format');
      const parsed = JSON.parse(jsonMatch?.[0]);
      setAdCopies(parsed?.adCopies || []);
      setAudienceSuggestions(parsed?.audienceSuggestions || {});
      setBudgetRecommendation(parsed?.budgetRecommendation || {});
      setCampaignName(parsed?.campaignName || prompt?.slice(0, 50));
    } catch (err) {
      toast?.error('Failed to generate ad copy. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleLaunch = async () => {
    if (!adCopies) { toast?.error('Generate ad copy first'); return; }
    setLaunching(true);
    try {
      const metaToken = organization?.meta_access_token;
      const metaAdAccountId = organization?.meta_ad_account_id;
      const googleRefreshToken = organization?.google_ads_refresh_token;
      const googleCustomerId = organization?.google_ads_customer_id;

      let externalCampaignId = null;
      let launchStatus = 'active';

      // Meta Marketing API v18
      if ((platform === 'facebook' || platform === 'instagram' || platform === 'all') && metaToken && metaAdAccountId) {
        try {
          const metaRes = await fetch(
            `https://graph.facebook.com/v18.0/act_${metaAdAccountId}/campaigns`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: campaignName,
                objective: 'LEAD_GENERATION',
                status: 'ACTIVE',
                special_ad_categories: [],
                access_token: metaToken,
              }),
            }
          );
          const metaData = await metaRes?.json();
          if (metaData?.id) externalCampaignId = metaData?.id;
          else if (metaData?.error) throw new Error(metaData.error.message);
        } catch (metaErr) {
          toast?.error(`Meta API: ${metaErr?.message || 'Launch failed'}`);
          launchStatus = 'error';
        }
      }

      // Google Ads API v17 — via Supabase Edge Function (secure proxy)
      if ((platform === 'google' || platform === 'all') && googleRefreshToken && googleCustomerId) {
        try {
          const { data: { session } } = await supabase?.auth?.getSession();
          const dailyBudgetMicros = budget ? Math.round(parseFloat(budget) * 1_000_000) : 10_000_000;

          const edgeRes = await fetch(
            `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/google-ads-campaigns`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({
                action: 'create_campaign',
                campaignName,
                dailyBudgetMicros,
                status: 'PAUSED',
              }),
            }
          );
          const edgeData = await edgeRes?.json();
          if (edgeData?.error) throw new Error(edgeData.error);
          if (edgeData?.resourceName) {
            if (!externalCampaignId) externalCampaignId = edgeData?.resourceName;
          }
        } catch (googleErr) {
          toast?.error(`Google Ads: ${googleErr?.message || 'Launch failed'}`);
          launchStatus = 'error';
        }
      }

      // Save to Supabase
      const { error } = await supabase?.from('ad_campaigns')?.insert({
        org_id: orgId,
        platform,
        external_campaign_id: externalCampaignId,
        name: campaignName,
        status: launchStatus,
        daily_budget: parseFloat(budget) || 0,
        location,
        prompt,
        ad_copies: adCopies,
        audience_suggestions: audienceSuggestions,
      });

      if (error) throw error;

      if (launchStatus === 'active') {
        toast?.success('Campaign launched successfully!');
      } else {
        toast?.success('Campaign saved (check API credentials to launch live)');
      }
      onCampaignCreated?.();
      setAdCopies(null);
      setAudienceSuggestions(null);
      setBudgetRecommendation(null);
      setPrompt('');
      setBudget('');
      setLocation('');
      setCampaignName('');
    } catch (err) {
      toast?.error(err?.message || 'Failed to launch campaign');
    } finally {
      setLaunching(false);
    }
  };

  const hasOAuthConnected = organization?.meta_access_token || organization?.google_ads_refresh_token;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Form */}
      <div className="space-y-5">
        {!hasOAuthConnected && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Connect Ad Accounts</p>
              <p className="text-xs text-amber-700 mt-0.5">Go to <strong>Integrations</strong> to connect Facebook Business or Google Ads to launch live campaigns.</p>
            </div>
          </div>
        )}

        {/* Platform Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Platform</label>
          <div className="grid grid-cols-2 gap-2">
            {PLATFORMS?.map((p) => (
              <button
                key={p?.id}
                onClick={() => setPlatform(p?.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  platform === p?.id ? p?.bg + ' ' + p?.color : 'border-border text-muted-foreground hover:border-primary/30'
                }`}
              >
                <Icon name={p?.icon} size={16} />
                {p?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Campaign Goal</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e?.target?.value)}
            placeholder="Describe your campaign goal, product/service, and target audience..."
            rows={4}
            className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 resize-none"
          />
        </div>

        {/* Budget + Location */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Daily Budget ($)</label>
            <input
              type="number"
              value={budget}
              onChange={(e) => setBudget(e?.target?.value)}
              placeholder="50"
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e?.target?.value)}
              placeholder="New York, 25mi"
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500"
            />
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating || !prompt?.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {generating ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Generating with Claude AI...</span></>
          ) : (
            <><Icon name="Sparkles" size={16} /><span>Generate Ad Copy</span></>
          )}
        </button>
      </div>

      {/* Right: Preview */}
      <div className="space-y-4">
        {!adCopies ? (
          <div className="flex flex-col items-center justify-center h-full min-h-64 border-2 border-dashed border-border rounded-xl text-center p-8">
            <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center mb-3">
              <Icon name="Sparkles" size={22} className="text-violet-500" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">AI-Generated Ad Copy</p>
            <p className="text-xs text-muted-foreground">Fill in the form and click Generate to see 3 ad variations</p>
          </div>
        ) : (
          <>
            {/* Ad Copies */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">3 Ad Variations</p>
              <div className="space-y-3">
                {adCopies?.map((ad, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-5 h-5 bg-violet-100 text-violet-700 rounded-full text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-sm font-semibold text-foreground">{ad?.headline}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{ad?.description}</p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                      <Icon name="MousePointerClick" size={11} />
                      {ad?.callToAction}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Audience */}
            {audienceSuggestions && (
              <div className="bg-card border border-border rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Suggested Audience</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Icon name="Users" size={13} className="text-muted-foreground" />
                    <span className="text-muted-foreground">Age:</span>
                    <span className="text-foreground font-medium">{audienceSuggestions?.ageRange}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {audienceSuggestions?.interests?.map((interest, i) => (
                      <span key={i} className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-xs">{interest}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Budget Recommendation */}
            {budgetRecommendation && (
              <div className="bg-card border border-border rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Budget Recommendation</p>
                <div className="flex gap-4 mb-2">
                  <div><span className="text-xs text-muted-foreground">Daily: </span><span className="text-sm font-bold text-foreground">{budgetRecommendation?.daily}</span></div>
                  <div><span className="text-xs text-muted-foreground">Monthly: </span><span className="text-sm font-bold text-foreground">{budgetRecommendation?.monthly}</span></div>
                </div>
                <p className="text-xs text-muted-foreground">{budgetRecommendation?.reasoning}</p>
              </div>
            )}

            <button
              onClick={handleLaunch}
              disabled={launching}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {launching ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Launching...</span></>
              ) : (
                <><Icon name="Rocket" size={16} /><span>Launch Campaign</span></>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateCampaignTab;
